import { Component, ElementRef, Input, AfterViewInit, Renderer2, TemplateRef, ViewChild } from '@angular/core';
import { BehaviorSubject, combineLatest, of } from 'rxjs';
import { first, map, shareReplay, switchMap } from 'rxjs/operators';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import {
  AzzurroEventConfigurationDetail,
  AzzurroEventDefaultConfig,
  EventModel,
  EventNotificationConfig,
  EventNotificationData,
  EventNotificationItemInput,
  EventsOperation,
  formatMinutesDuration,
  UserPermissions,
} from 'ngx-core-services';
import { ToastFacade, UiPromiseService, ColumnConfig, ColumnTypeEnum, PaginationConfig } from 'ngx-zcs-components';

import { SystemDefinition } from '../../store';
import { NotificationConfigurationService } from './notification-configuration.service';
import { labels } from '../create-block/create.model';
import { CreateCustomBlockSizeType, ICustomBlock } from '../decorators';
import { updateEventModelsTranslations } from './event-utils';
import {
  ConfiguredEventRow,
  EventFormField,
  EventModelsTranslations,
  NotificationConfigurationListItem,
  NotificationEventRow,
  TIME_FIELD_RANGES,
  TimeFieldRange,
  TimeUnit,
  getConfiguredEventModels,
  getDevicesByCode,
  getEventPayload,
  getSystemEventsTypes,
} from './notification-configuration.model';

@CreateCustomBlockSizeType({
  'breakpoint-900': 900,
})
@Component({
  selector: 'lib-notification-configuration',
  templateUrl: './notification-configuration.component.html',
  styleUrls: ['./notification-configuration.component.scss'],
})
export class NotificationConfigurationComponent implements ICustomBlock, AfterViewInit {
  @ViewChild('actionTemplate', { static: false }) actionTemplate?: TemplateRef<any>;
  @ViewChild('actionHeaderTemplate', { static: true }) actionHeaderTemplate!: TemplateRef<any>;
  @ViewChild('eventDevicesTemplate', { static: false }) eventDevicesTemplate?: TemplateRef<any>;
  @ViewChild('eventConfigFormTemplate', { static: true }) eventConfigFormTemplate!: TemplateRef<any>;
  private columnsInitialized: boolean = false;
  private readonly eventRowStateByName = new Map<EventModel, NotificationEventRow>();
  private readonly configuredEventRowStateByName = new Map<EventModel, ConfiguredEventRow>();
  private readonly timeFieldState = new Map<string, { value: number | null; unit: TimeUnit; minutes: number | null }>();
  private readonly timeFieldRanges: Partial<Record<EventFormField, TimeFieldRange>> = TIME_FIELD_RANGES;
  formatMinutesDurationLocalized(minutes: number | null): string {
    return formatMinutesDuration(minutes, {
      days: this.labels.timeUnitDaysShort ?? 'd',
      hours: this.labels.timeUnitHoursShort ?? 'h',
      minutes: this.labels.timeUnitMinutesShort ?? 'm',
    });
  }
  readonly system$ = new BehaviorSubject<SystemDefinition | undefined>(undefined);
  @Input() set system(system: SystemDefinition | undefined) {
    if (!system || !system.uid) {
      return;
    }
    this.system$.next(system);
    this.notificationConfigurationService.getAzzurroEventsConfigurationByPlantId(system.uid).then((eventConfig) => {
      this.plantEventConfigurations$.next(eventConfig);
    });
  }

  readonly _labels = labels;
  @Input() set labels(value: Partial<typeof this._labels>) {
    if (!value) {
      return;
    }
    const hasChanges = Object.keys(value).some(
      (key) => this._labels[key as keyof typeof this._labels] !== value[key as keyof typeof this._labels]
    );
    if (!hasChanges) {
      return;
    }
    Object.assign(this._labels, value);
    this.eventModelsTranslations = updateEventModelsTranslations(this._labels);
    this.timeUnitOptions = this.buildTimeUnitOptions();
    this.updateColumns(true);
  }

  get labels(): typeof this._labels {
    return this._labels;
  }

  readonly notificationConfigurationsList$ = combineLatest([
    this.notificationConfigurationService.getAzzurroEventsConfigurationList(),
    this.system$.pipe(
      map((system) => getSystemEventsTypes(system)),
    ),
  ]).pipe(
    map(([eventConfigurations, systemInverterTypes]) => {
      return eventConfigurations.filter((eventConfiguration) => {
        const generations = eventConfiguration.generations ?? [];
        if (generations.length === 0) {
          return false;
        }
        return generations.some((generation) => systemInverterTypes.includes(generation));
      });
    }),
    map((eventConfigurations) =>
      eventConfigurations.map((eventConfiguration) => {
        const detail = eventConfiguration as Partial<AzzurroEventConfigurationDetail>;
        return {
          eventModelName: detail.eventName,
          payloadType: detail.payloadType,
          defaultConfig: detail.defaultConfig,
          alertCodes: detail.alertCodes,
          deviceByCode: getDevicesByCode(detail.alertCodes, this.system$.value!),
        } as NotificationConfigurationListItem;
      })
    ),
  );

  readonly selectedNotificationDetail$ = new BehaviorSubject<EventModel | undefined>(undefined);

  readonly plantEventConfigurations$ = new BehaviorSubject<Partial<EventNotificationData> | undefined>(undefined);

  readonly users$ = combineLatest([
    this.system$,
    this.plantEventConfigurations$
  ]).pipe(
    map(([system, plantEventConfigurations]) => {
      this.userForm.get('users')?.setValue(plantEventConfigurations?.emails || []);
      return system?.aws?.users?.filter((user) => user.userLevel !== UserPermissions.hidden) || [];
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly selectedEventModels$ = this.plantEventConfigurations$.pipe(
    map((plantEventConfigurations) => getConfiguredEventModels(plantEventConfigurations?.events)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly unselectedEventModels$ = combineLatest([
    this.notificationConfigurationsList$,
    this.plantEventConfigurations$,
  ]).pipe(
    map(([eventConfigurations, plantEventConfigurations]) => {
      const configuredEventModels = getConfiguredEventModels(plantEventConfigurations?.events);
      return eventConfigurations.filter((eventConfiguration) => {
        return !configuredEventModels.includes(eventConfiguration.eventModelName!);
      });
    }),
    map((eventConfigurations) =>  eventConfigurations.map((eventConfiguration) => this.getOrCreateEventRowState(eventConfiguration))),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly unselectedEventModelsSelections$ = this.unselectedEventModels$.pipe(
    switchMap((eventModels) =>
      eventModels?.length
        ? combineLatest(eventModels.map((eventModel) => eventModel.selected$))
        : of([]),
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly selectAllState$ = combineLatest([
    this.unselectedEventModels$,
    this.unselectedEventModelsSelections$,
  ]).pipe(
    map(([eventModels, selections]) => {
      const total = eventModels?.length ?? 0;
      if (total === 0) {
        return 'unchecked';
      }
      const selectedCount = selections.filter(Boolean).length;
      if (selectedCount === 0) {
        return 'unchecked';
      }
      if (selectedCount === total) {
        return 'checked';
      }
      return 'indeterminate';
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly selectedConfiguredEvent$ = combineLatest([
    this.selectedNotificationDetail$,
    this.plantEventConfigurations$,
    this.notificationConfigurationsList$,
  ]).pipe(
    map(([eventName, plantEventConfigurations, eventConfigurations]) => {
      if (!eventName) {
        return null;
      }
      const defaultConfig = eventConfigurations.find((eventConfiguration) => eventConfiguration.eventModelName === eventName)
        ?.defaultConfig;
      const payload = getEventPayload(plantEventConfigurations?.events ?? null, eventName);
      return this.getOrCreateConfiguredEventRow(eventName, payload, defaultConfig);
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  userForm: FormGroup = this.fb.group({
    users: new FormControl([], Validators.required),
  });

  eventModelsTranslations: EventModelsTranslations;
  columns: ColumnConfig[] = [];
  readonly eventFormLabels = this.labels;
  timeUnitOptions: Array<{ value: TimeUnit; label: string; multiplier: number }> = [];
  private readonly timeFields = new Set<EventFormField>([
    'activeTimeMinutes',
    'recurringEmailIntervalMinutes',
    'snoozeDurationMinutes',
    'sleepModeDurationMinutes',
  ]);
  readonly eventRowExpansionByRow = (row?: NotificationEventRow | null) =>
    row?.form ? this.eventConfigFormTemplate : null;
  readonly eventRowKey = (row?: NotificationEventRow | null) => row?.eventName ?? '';
  paginationConfig: PaginationConfig = {
    pageSize: 5,
    pageSizeOptions: [5, 10, 25, 50],
  };
  eventModelsFilterPredicate = (row: { eventName?: EventModel }, filter: string): boolean => {
    if (!filter) {
      return true;
    }
    const eventName = row.eventName;
    if (!eventName) {
      return false;
    }
    const translated = this.eventModelsTranslations[eventName as keyof EventModelsTranslations];
    const name = translated?.translateName || eventName;
    const description = translated?.translateDescription || '';
    const normalized = filter.trim().toLowerCase();
    return name.toLowerCase().includes(normalized) || description.toLowerCase().includes(normalized);
  };

  constructor(
    private readonly fb: FormBuilder,
    private readonly notificationConfigurationService: NotificationConfigurationService,
    private readonly uiPromiseService: UiPromiseService,
    private readonly toastFacade: ToastFacade,
    readonly el: ElementRef,
    readonly renderer: Renderer2,
  ) {
    this.saveConfiguration = this.saveConfiguration.bind(this);
    this.eventModelsTranslations = updateEventModelsTranslations(this.labels);
    this.timeUnitOptions = this.buildTimeUnitOptions();
  }

  ngAfterViewInit(): void {
    this.updateColumns();
  }

  ensureRowSelected(row: NotificationEventRow): void {
    if (!row?.selected$?.value) {
      row.toggleSelection();
    }
  }

  toggleNotificationSelection(notification: EventModel): void {
    this.selectedNotificationDetail$.next(
      notification === this.selectedNotificationDetail$.getValue() ? undefined : notification
    );
  }

  private async applyManageAzzurroEventPayload(
    payload: Partial<EventNotificationConfig> | null | undefined
  ): Promise<EventNotificationData | undefined> {
    const updatedConfig = payload?.data;
    if (updatedConfig) {
      this.plantEventConfigurations$.next(updatedConfig);
    }
    if (payload?.success === false) {
      await this.toastFacade.present(this.labels.genericErrorMessage, undefined, {
        severity: 'error',
        mobileMode: 'single',
        browserMode: 'simple',
      });
    }
    return updatedConfig;
  }

  updateConfiguredEvent(row: ConfiguredEventRow | null) {
    return async () => {
      if (!row?.eventName) {
        return;
      }
      const plantId = this.system$.value?.uid;
      if (!plantId) {
        return;
      }
      this.uiPromiseService.run(async () => {
        try {
          const deleteInput: EventNotificationItemInput = {
            [row.eventName]: {},
          } as EventNotificationItemInput;
          const deleteResponse = await this.notificationConfigurationService.manageAzzurroEvent(
            plantId,
            this.userForm.get('users')?.value ?? [],
            deleteInput,
            EventsOperation.delete
          );
          await this.applyManageAzzurroEventPayload(deleteResponse?.data?.manageAzzurroEvent);
          const eventsInput: EventNotificationItemInput = {
            [row.eventName]: row.form.value ?? {},
          } as EventNotificationItemInput;
          const response = await this.notificationConfigurationService.manageAzzurroEvent(
            plantId,
            this.userForm.get('users')?.value ?? [],
            eventsInput,
            EventsOperation.update
          );
          await this.applyManageAzzurroEventPayload(response?.data?.manageAzzurroEvent);
        } catch (_error) {
          await this.toastFacade.present(this.labels.genericErrorMessage, undefined, {
            severity: 'error',
            mobileMode: 'single',
            browserMode: 'simple',
          });
        }
      }, { loading: true });
    };
  }

  deleteConfiguration(eventModel: EventModel) {
    return async () => {
      const plantId = this.system$.value?.uid;
      if (plantId) {
        this.uiPromiseService.run(async () => {
          try {
            const eventsInput: EventNotificationItemInput = {
              [eventModel]: {},
            } as EventNotificationItemInput;
            const response = await this.notificationConfigurationService.manageAzzurroEvent(
              plantId,
            this.userForm.get('users')?.value ?? [],
            eventsInput,
            EventsOperation.delete
          );
          this.selectedNotificationDetail$.next(undefined);
          const updatedConfig = await this.applyManageAzzurroEventPayload(response?.data?.manageAzzurroEvent);
          if (updatedConfig) {
            return;
          }
          const refreshedConfig = await this.notificationConfigurationService.getAzzurroEventsConfigurationByPlantId(plantId);
          this.plantEventConfigurations$.next(refreshedConfig);
        } catch (_error) {
          await this.toastFacade.present(this.labels.genericErrorMessage, undefined, {
            severity: 'error',
              mobileMode: 'single',
              browserMode: 'simple',
            });
          }
        }, { loading: true });
      }
    }
  }

  async saveConfiguration() {
    const plantId = this.system$.value?.uid;
    if (plantId) {
      const emailList = this.userForm.get('users')?.value;
      const selectedEventModels = (await this.unselectedEventModels$.pipe(first()).toPromise())
        .filter((eventModel) => eventModel.selected$.value);

      selectedEventModels.forEach((eventModel) => {
        const eventName = eventModel.eventName ?? 'unknown';
        const values = eventModel.form?.value ?? {};
        console.log('[notification-configuration] selected event form values', eventName, values);
      });

      this.uiPromiseService.run(async () => {
        try {
          const eventsInput = selectedEventModels.reduce<EventNotificationItemInput>((acc, eventModel) => {
            if (!eventModel.eventName) {
              return acc;
            }
            acc[eventModel.eventName] = eventModel.form?.value ?? {};
            return acc;
          }, {});
          const hasSelectedEvents = Object.keys(eventsInput).length > 0;
          const response = await this.notificationConfigurationService.manageAzzurroEvent(
            plantId,
            emailList,
            hasSelectedEvents ? eventsInput : undefined
          );
          await this.applyManageAzzurroEventPayload(response?.data?.manageAzzurroEvent);
        } catch (_error) {
          await this.toastFacade.present(this.labels.genericErrorMessage, undefined, {
            severity: 'error',
            mobileMode: 'single',
            browserMode: 'simple',
          });
        }
    }, { loading: true });
    }
  }

  private updateColumns(force: boolean = false): void {
    if (!this.actionTemplate || !this.eventDevicesTemplate || !this.actionHeaderTemplate) {
      return;
    }
    if (this.columnsInitialized && !force) {
      return;
    }
    this.columns = [
      {
        columnDef: 'eventModelName',
        header: this.labels.notificationTableHeaderEventModelName,
        type: ColumnTypeEnum.Text,
        sortable: true,
        cell: (row: { eventName?: string }) => {
          const eventName = row.eventName || '';
          const translated = this.eventModelsTranslations[eventName as keyof EventModelsTranslations];
          return translated?.translateName || eventName;
        },
      },
      {
        columnDef: 'eventModelDescription',
        header: this.labels.notificationTableHeaderDescription,
        type: ColumnTypeEnum.Text,
        sortable: true,
        cell: (row: { eventName?: string }) => {
          const eventName = row.eventName || '';
          const translated = this.eventModelsTranslations[eventName as keyof EventModelsTranslations];
          return translated?.translateDescription || '';
        },
      },
      {
        columnDef: 'eventDevices',
        header: this.labels.associatedAlarm,
        type: ColumnTypeEnum.Template,
        sortable: false,
        template: this.eventDevicesTemplate,
      },
      {
        columnDef: 'action',
        sortable: false,
        header: this.labels.notificationTableHeaderActions,
        headerTemplate: this.actionHeaderTemplate,
        type: ColumnTypeEnum.Template,
        template: this.actionTemplate,
        headerClass: 'align-end',
        cellClass: 'align-end',
      },
    ];
    this.columnsInitialized = true;
  }

  private getTimeFieldKey(row: NotificationEventRow, field: EventFormField): string {
    return `${row.eventName ?? 'unknown'}:${field}`;
  }

  private getTimeUnitMultiplier(unit: TimeUnit): number {
    return this.timeUnitOptions.find((option) => option.value === unit)?.multiplier ?? 1;
  }

  private normalizeMinutes(value: number | string | null | undefined): number | null {
    if (value === null || value === undefined) {
      return null;
    }
    const numberValue = Number(value);
    if (Number.isNaN(numberValue)) {
      return null;
    }
    return numberValue;
  }

  private deriveTimeState(minutes: number | null) {
    if (minutes === null || Number.isNaN(minutes)) {
      return { value: null, unit: 'minutes' as TimeUnit, minutes: null };
    }
    if (minutes % 1440 === 0) {
      return { value: minutes / 1440, unit: 'days' as TimeUnit, minutes };
    }
    if (minutes % 60 === 0) {
      return { value: minutes / 60, unit: 'hours' as TimeUnit, minutes };
    }
    return { value: minutes, unit: 'minutes' as TimeUnit, minutes };
  }

  private buildTimeUnitOptions(): Array<{ value: TimeUnit; label: string; multiplier: number }> {
    return [
      { value: 'minutes', label: this.labels.timeUnitMinutes, multiplier: 1 },
      { value: 'hours', label: this.labels.timeUnitHours, multiplier: 60 },
      { value: 'days', label: this.labels.timeUnitDays, multiplier: 1440 },
    ];
  }

  toggleAllSelection(checked: boolean) {
    this.unselectedEventModels$.pipe(first()).subscribe((eventModels) => {
      eventModels?.forEach((eventModel) => eventModel.selected$.next(checked));
    });
  }

  getEventFormTooltip(field: EventFormField): string {
    const tooltipKey = `${field}Tooltip` as keyof typeof this.labels;
    return (this.labels[tooltipKey] as string) || this.eventFormLabels[field] || '';
  }

  isTimeField(field: EventFormField): boolean {
    return this.timeFields.has(field);
  }

  getTimeFieldState(row: NotificationEventRow, field: EventFormField) {
    const key = this.getTimeFieldKey(row, field);
    const minutes = this.normalizeMinutes(row.form?.get(field)?.value);
    const existing = this.timeFieldState.get(key);
    if (!existing || existing.minutes !== minutes) {
      const derived = this.deriveTimeState(minutes);
      this.timeFieldState.set(key, derived);
      return derived;
    }
    return existing;
  }

  getTimeFieldRangeError(field: EventFormField): string {
    const range = this.timeFieldRanges[field];
    if (!range) {
      return '';
    }
    const template = this.labels.notificationValueBetweenMinutes || 'Value must be between {{ min }} and {{ max }} minutes';
    return template.replace('{{ min }}', String(range.min)).replace('{{ max }}', String(range.max));
  }

  onTimeValueChange(row: NotificationEventRow, field: EventFormField, value: number | null | string): void {
    const key = this.getTimeFieldKey(row, field);
    const state = this.getTimeFieldState(row, field);
    const normalizedValue = value === null || value === undefined || value === '' ? null : Number(value);
    const minutes = normalizedValue === null || Number.isNaN(normalizedValue)
      ? null
      : normalizedValue * this.getTimeUnitMultiplier(state.unit);
    const control = row.form?.get(field);
    control?.setValue(minutes);
    control?.markAsDirty();
    control?.markAsTouched();
    control?.updateValueAndValidity({ emitEvent: false });
    this.timeFieldState.set(key, {
      value: normalizedValue,
      unit: state.unit,
      minutes: this.normalizeMinutes(minutes),
    });
  }

  onTimeUnitChange(row: NotificationEventRow, field: EventFormField, unit: TimeUnit): void {
    const key = this.getTimeFieldKey(row, field);
    const state = this.getTimeFieldState(row, field);
    const minutes = state.value === null || Number.isNaN(state.value)
      ? null
      : state.value * this.getTimeUnitMultiplier(unit);
    const control = row.form?.get(field);
    control?.setValue(minutes);
    control?.markAsDirty();
    control?.markAsTouched();
    control?.updateValueAndValidity({ emitEvent: false });
    this.timeFieldState.set(key, {
      value: state.value,
      unit,
      minutes: this.normalizeMinutes(minutes),
    });
  }

  getEventFormFields(row: { form?: FormGroup } | null | undefined): EventFormField[] {
    const controls = row?.form?.controls;
    if (!controls) {
      return [];
    }
    return Object.keys(controls) as EventFormField[];
  }

  private getOrCreateEventRowState(eventConfiguration: NotificationConfigurationListItem): NotificationEventRow {
    const eventName = eventConfiguration.eventModelName;
    if (!eventName) {
      return this.createEventRowState(eventConfiguration);
    }
    const existing = this.eventRowStateByName.get(eventName);
    if (existing) {
      existing.deviceByCode = eventConfiguration.deviceByCode;
      existing.payloadType = eventConfiguration.payloadType;
      existing.defaultConfig = eventConfiguration.defaultConfig;
      return existing;
    }
    const created = this.createEventRowState(eventConfiguration);
    this.eventRowStateByName.set(eventName, created);
    return created;
  }

  private createEventRowState(eventConfiguration: NotificationConfigurationListItem): NotificationEventRow {
    const eventName = eventConfiguration.eventModelName;
    const selected$ = new BehaviorSubject<boolean>(false);
    return {
      eventName,
      deviceByCode: eventConfiguration.deviceByCode,
      payloadType: eventConfiguration.payloadType,
      defaultConfig: eventConfiguration.defaultConfig,
      form: this.buildEventForm(eventConfiguration.defaultConfig),
      selected$,
      toggleSelection: () => selected$.next(!selected$.value),
    };
  }

  private buildEventForm(
    defaultConfig?: AzzurroEventDefaultConfig,
    initialValues?: Partial<AzzurroEventDefaultConfig> | null
  ): FormGroup {
    const fields = Object.entries(defaultConfig ?? {})
      .filter(([field, value]) => field !== '__typename' && value !== null && value !== undefined)
      .map(([field]) => field as EventFormField);
    const group = fields.reduce<Record<string, FormControl>>((acc, field) => {
      const value = initialValues?.[field] ?? defaultConfig?.[field] ?? null;
      const validators = [];
      const range = this.timeFieldRanges[field];
      if (range) {
        validators.push(Validators.min(range.min), Validators.max(range.max));
      }
      acc[field] = new FormControl(value, validators);
      return acc;
    }, {});
    return this.fb.group(group);
  }

  private getOrCreateConfiguredEventRow(
    eventName: EventModel,
    payload: Partial<AzzurroEventDefaultConfig> | null,
    defaultConfig?: AzzurroEventDefaultConfig
  ): ConfiguredEventRow {
    const existing = this.configuredEventRowStateByName.get(eventName);
    const initialValues = payload;
    if (existing) {
      if (initialValues) {
        existing.form.patchValue(initialValues, { emitEvent: false });
      }
      return existing;
    }
    const form = this.buildEventForm(defaultConfig, initialValues);
    const created = { eventName, form };
    this.configuredEventRowStateByName.set(eventName, created);
    return created;
  }
}
